import { CdkMenu, CdkMenuBase, CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { Subscription, concat, of } from 'rxjs';
import { Territory } from 'src/app/models/territory';
import { AclService } from 'src/app/services/acl.service';
import { MessageService } from 'src/app/services/message.service';
import { TerritoryService } from 'src/app/services/territory.service';

type TerritoryOption = Territory & { selected: boolean }
type TerritorySelectedOption = { value: string, selected: boolean, display?: boolean }

@Component({
  selector: 'app-territory-select',
  templateUrl: './territory-select.component.html',
  styleUrls: ['./territory-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TerritorySelectComponent),
      multi: true,
    },
  ],
})
export class TerritorySelectComponent implements ControlValueAccessor, OnChanges, OnInit {
  private id: string;
  @Input() label?: string;
  @Input() help?: string;
  @Input() labelOutside: boolean = false;
  @Input() search: boolean = false;
  @Input() multiple: boolean = false;
  @Input() disabled: boolean = false;
  @Input() class?: string;
  @Input() hint?: string;
  @Input() error: { value: boolean, message?: string } = {
    value: false,
    message: undefined
  };
  @Input() bottomWrapper: boolean = false;
  @Input() emitOnInit: boolean = true;
  /** Every territory, even for a Country Operator (whose list is otherwise limited to their own countries). */
  @Input() unrestricted: boolean = false;
  /** Territory codes to offer (e.g. the countries of a case); empty offers every territory. */
  @Input() restrict: string[] = [];

  territories: TerritoryOption[] = [];
  selected: TerritorySelectedOption[] = [];

  _filter?: string;

  @Output() change = new EventEmitter<any>();
  @Input() value?: string | string[];
  @Output() valueChange = new EventEmitter<any>();

  show: boolean = false;

  selection?: string;

  subscription: Subscription;

  @ViewChild('trigger') trigger?: CdkMenuTrigger;

  countries: string[] = [];

  private initialized: boolean = false;

  // ControlValueAccessor — additive, coexists with [(value)] usages.
  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(
    private _message: MessageService,
    private aclService: AclService,
    private territoryService: TerritoryService,
    private translate: TranslateService
  ) {
    this.id = _.uniqueId("SELECT-");
    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "LAYOUT.SELECT") {
        (change.data.id !== this.id) && (this.show = false);
      }
    });
  }

  ngOnInit() {
    this.countries = this.unrestricted ? [] : this.aclService.countries();
    this.initialized = true;
    this.setInitialState();
  }

  ngOnChanges(changes: SimpleChanges) {
    // the restriction can arrive after the first load (e.g. a case's territories)
    if (this.initialized && changes['restrict']
      && !_.isEqual(changes['restrict'].currentValue, changes['restrict'].previousValue)) {
      this.retrieveTerritories();
    }
  }

  _isSelected(option: string) {
    return this.selected.map((t) => t.value).includes(option);
  }

  async populate() {
    const territories = await this.territoryService.retrieve();
    if (this.multiple && this.value && (this.value as string[]).length > 0) {
      const values = (this.value as string[]);
      const matching = territories.filter((t) => _.intersection([t.id, ...t.alternateCodes], values).length > 0)
      this.selected = matching.map((t) => ({ value: t.id, selected: true, display: true }));
    } else if (!this.multiple && this.value) {
      const value = (this.value as string);
      const matching = territories.find((t) => _.intersection([t.id, ...t.alternateCodes], [value]).length > 0);
      if (matching)
        this.selected = [{ value: matching.id, selected: true, display: true }];
    }
  }

  selectedToDisplay() {
    return this.selected.filter((o) => o.display);
  }

  async setInitialState() {
    await this.retrieveTerritories();
    if (this.multiple && this.value && (this.value as string[]).length > 0) {
      const values = (this.value as string[]);

      // collapse alternate codes into their parent territory - one entry per territory
      const selectedIds = new Set<string>();
      for (const code of values) {
        const territory = this.territories.find(t =>
          t.id === code || (t.alternateCodes?.includes(code))
        );
        selectedIds.add(territory ? territory.id : code);
      }

      this.selected = [...selectedIds].map(id => ({
        value: id,
        selected: true,
      }));

      this.territories.forEach(territory => {
        territory.selected = selectedIds.has(territory.id);
      });

      if (this.selected.length > 0) {
        this.selection = this.selected.length === 1
          ? this.translate.instant(`TERRITORIES.${this.selected[0].value}`)
          : `${this.label} (${this.selected.length})`;
      } else {
        this.selection = undefined;
      }
    } else if (!this.multiple && this.value) {
      const value = (this.value as string);
      this.selected = [{ value, selected: true }];
      const option = this.territories.find(t => t.id == value || t.alternateCodes.includes(value));
      if (option) {
        option.selected = true;
      }
      this.selection = value ? this.translate.instant(`TERRITORIES.${value}`) : undefined;
    } else {
      // No value (form reset / setValue(null)): drop any stale selection.
      this.territories.forEach((t) => (t.selected = false));
      this.selected = [];
      this.selection = undefined;
    }
    if (this.emitOnInit) {
      this.onChange();
    }
  }

  removeFromPreselection(option: TerritorySelectedOption) {
    this.selected.splice(this.selected.findIndex((o) => option.value === o.value), 1);
    const doc = this.territories.find((t) => t.id === option.value);
    if (doc) {
      doc.selected = false;
    }
    this.onChange()
  }

  toggleFromSelection(option: TerritoryOption) {
    if (!option.selected) {
      this.selected.splice(this.selected.findIndex((o) => option.id === o.value), 1);
    } else if (!this._isSelected(option.id)) {
      this.selected.push({ value: option.id, selected: true, display: false });
    }
  }

  async retrieveTerritories() {
    this.territories = (await this.territoryService.retrieve()).map(territory => ({ ...territory, selected: this._isSelected(territory.id) }));
    if (this.countries && this.countries.length > 0) {
      this.territories = this.territories.filter(territory => this.countries.includes(territory.code));
    }
    if (this.restrict && this.restrict.length > 0) {
      this.territories = this.territories.filter(territory => this.restrict.includes(territory.code));
    }
    this.territories.sort((a, b) => this.translate.instant(`TERRITORIES.${a.id}`).localeCompare(this.translate.instant(`TERRITORIES.${b.id}`)))
  }

  formatSelection() {
    if (!this.multiple) {
      const value = this.territories.find(c => c.selected);
      return value ? this.translate.instant(`TERRITORIES.${value.id}`) : undefined;
    } else {
      const value = this.territories.filter(c => c.selected);
      if (value.length > 0) {
        return value.length === 1 ? this.translate.instant(`TERRITORIES.${value[0].id}`) : `${this.label} (${value.length})`;
      } else {
        return undefined;
      }
    }
  }

  select(option: any) {
    option.selected = option.selected !== null ? !option.selected : true;
    this.toggleFromSelection(option)
    if (!this.multiple) {
      this.territories.forEach(c => {
        if (c.id !== option.id) {
          c.selected = false;
        }
      })
    }
    this.onChange();
  }

  async onChange() {
    this._onTouched();
    if (!this.multiple) {
      const value = this.territories.find(c => c.selected);
      this.selection = value ? this.translate.instant(`TERRITORIES.${value.id}`) : undefined;
      this.valueChange.emit(value?.id as string);
      this.change.emit({ value });
      this._onChange(value?.id as string);
      this.trigger?.close();
    } else {
      const values = _.unionWith([...this.territories.filter(c => c.selected).map(c => c.id), ...this.selected.map((o) => o.value)]);
      if (values.length > 0) {
        this.selection = values.length === 1 ? this.translate.instant(`TERRITORIES.${values[0]}`) : `${this.label} (${values.length})`;
      } else {
        this.selection = undefined;
      }
      this.valueChange.emit(values as string[]);
      this.change.emit({ value: this.territories.filter((t) => values.includes(t.id)) });
      this._onChange(values as string[]);

    }

  }

  // ── ControlValueAccessor ───────────────────────────────────────────────────

  writeValue(value: string | string[] | undefined): void {
    this.value = value;
    // Reconcile internal selection state without emitting back to the form.
    const previousEmitOnInit = this.emitOnInit;
    this.emitOnInit = false;
    this.setInitialState();
    this.emitOnInit = previousEmitOnInit;
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }


  toggle() {
    this.show = !this.show;
    if (this.show) {
      this._message.emitChange("LAYOUT", "SELECT", {
        id: this.id
      });
    }
  }



  filtered() {
    return (this._filter && this._filter.length > 0) ? this.territories.filter(territory => {
      return this.translate.instant(`TERRITORIES.${territory.id}`).toLowerCase().includes(this._filter?.toLowerCase())
    }) : this.territories;
  }


  onClear() {
    if (this.multiple) {
      this.value = [];
    } else {
      this.value = undefined;
    }
    this.territories.forEach(territory => {
      territory.selected = false
    });
    this.selected = [];
    this.selection = undefined;
    this._filter = undefined;
    this.onChange();
    this.trigger?.close();
  }

  display() {
    if (this.selection && this.multiple && this.value) {
      return `${this.label} (${(this.value as string[]).length})`
    } else if (this.selection && !this.multiple && this.value) {
      return this.selection;
    } else {
      return undefined;
    }
  }
}
